import React, { useRef, useState, useEffect, useCallback } from "react";

// ── Constantes ───────────────────────────────────────
const COLORS = [
  "#1e293b", "#ef4444", "#2563eb", "#16a34a",
  "#f59e0b", "#7c3aed", "#ec4899", "#64748b",
];
const SIZES = [1.5, 3, 6, 12];
const FONT_SIZES = { 1.5: 16, 3: 22, 6: 34, 12: 52 };
const FONTS = [
  { id: "sans",  label: "Sans-serif",  css: "Arial, sans-serif" },
  { id: "serif", label: "Serif",       css: "'Times New Roman', serif" },
  { id: "mono",  label: "Monospace",   css: "'Courier New', monospace" },
];
const TOOLS = [
  { id: "pencil",   icon: "bi-pencil",    label: "Crayon (libre)" },
  { id: "line",     icon: "bi-slash-lg",  label: "Ligne (corrigée)" },
  { id: "rect",     icon: "bi-square",    label: "Rectangle" },
  { id: "circle",   icon: "bi-circle",    label: "Cercle" },
  { id: "triangle", icon: "bi-triangle",  label: "Triangle" },
  { id: "text",     icon: "bi-fonts",     label: "Texte (A, B, 1, 2…)" },
  { id: "eraser",   icon: "bi-eraser",    label: "Gomme" },
];

const SNAP_DEG = 15;

function snapEnd(x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len < 3) return { x: x2, y: y2 };
  const deg = Math.atan2(dy, dx) * 180 / Math.PI;
  const snapped = Math.round(deg / SNAP_DEG) * SNAP_DEG;
  const rad = snapped * Math.PI / 180;
  return { x: x1 + len * Math.cos(rad), y: y1 + len * Math.sin(rad) };
}

function isAlmostStraight(pts) {
  if (pts.length < 3) return true;
  const x1 = pts[0].x, y1 = pts[0].y;
  const x2 = pts[pts.length - 1].x, y2 = pts[pts.length - 1].y;
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len < 10) return false;
  const maxDist = pts.reduce((m, p) => {
    const d = Math.abs((y2 - y1) * p.x - (x2 - x1) * p.y + x2 * y1 - y2 * x1) / len;
    return Math.max(m, d);
  }, 0);
  return maxDist / len < 0.12;
}

function getPos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const sx = canvas.width / rect.width;
  const sy = canvas.height / rect.height;
  const src = e.touches ? e.touches[0] : e;
  return { x: (src.clientX - rect.left) * sx, y: (src.clientY - rect.top) * sy };
}

// ── Composant ────────────────────────────────────────
export default function DrawingCanvas({ onSend, onClose }) {
  const canvasRef    = useRef(null);
  const overlayRef   = useRef(null);
  const areaRef      = useRef(null);   // conteneur du canvas (pour positionner l'input texte)
  const textInputRef = useRef(null);
  const historyRef   = useRef([]);
  const drawing      = useRef(false);
  const startPt      = useRef({ x: 0, y: 0 });
  const ptsRef       = useRef([]);

  const [tool,        setTool]        = useState("pencil");
  const [color,       setColor]       = useState("#1e293b");
  const [size,        setSize]        = useState(3);
  const [font,        setFont]        = useState("sans");
  const [autoFix,     setAutoFix]     = useState(true);
  const [canUndo,     setCanUndo]     = useState(false);
  // Overlay texte : { canvasX, canvasY, screenX, screenY, value }
  const [textOverlay, setTextOverlay] = useState(null);

  const fontCss = FONTS.find(f => f.id === font)?.css || "Arial, sans-serif";
  const fontSize = FONT_SIZES[size] || 22;

  // ── Init canvas blanc ────────────────────────────
  useEffect(() => {
    const c = canvasRef.current.getContext("2d");
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  }, []);

  // ── Auto-focus sur l'input texte ─────────────────
  useEffect(() => {
    if (textOverlay) {
      setTimeout(() => textInputRef.current?.focus(), 30);
    }
  }, [textOverlay]);

  // ── Getters de contexte ──────────────────────────
  const getCtx = useCallback((forEraser = false) => {
    const c = canvasRef.current.getContext("2d");
    c.strokeStyle = forEraser ? "#ffffff" : color;
    c.fillStyle   = color;
    c.lineWidth   = forEraser ? size * 4 : size;
    c.lineCap = "round"; c.lineJoin = "round";
    return c;
  }, [color, size]);

  const getOvCtx = useCallback(() => {
    const c = overlayRef.current.getContext("2d");
    c.strokeStyle = color; c.lineWidth = size;
    c.lineCap = "round"; c.lineJoin = "round";
    return c;
  }, [color, size]);

  const clearOverlay = () => overlayRef.current.getContext("2d").clearRect(0, 0, 900, 500);

  // ── Historique ───────────────────────────────────
  const saveHistory = useCallback(() => {
    const snap = canvasRef.current.getContext("2d").getImageData(0, 0, 900, 500);
    historyRef.current.push(snap);
    if (historyRef.current.length > 30) historyRef.current.shift();
    setCanUndo(true);
  }, []);

  const undo = () => {
    if (!historyRef.current.length) return;
    canvasRef.current.getContext("2d").putImageData(historyRef.current.pop(), 0, 0);
    setCanUndo(historyRef.current.length > 0);
    setTextOverlay(null);
  };

  const clear = () => {
    saveHistory();
    const c = canvasRef.current.getContext("2d");
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, 900, 500);
    setTextOverlay(null);
  };

  // ── Formes ──────────────────────────────────────
  const drawShape = useCallback((c, t, x1, y1, x2, y2) => {
    c.beginPath();
    if (t === "rect") {
      c.rect(x1, y1, x2 - x1, y2 - y1);
    } else if (t === "circle") {
      const rx = (x2 - x1) / 2, ry = (y2 - y1) / 2;
      c.ellipse(x1 + rx, y1 + ry, Math.abs(rx), Math.abs(ry), 0, 0, 2 * Math.PI);
    } else if (t === "triangle") {
      c.moveTo((x1 + x2) / 2, y1);
      c.lineTo(x2, y2);
      c.lineTo(x1, y2);
      c.closePath();
    } else if (t === "line") {
      const { x: ex, y: ey } = snapEnd(x1, y1, x2, y2);
      c.moveTo(x1, y1); c.lineTo(ex, ey);
    }
    c.stroke();
  }, []);

  // ── Valider le texte et le dessiner ──────────────
  const confirmText = useCallback(() => {
    if (!textOverlay) return;
    const val = textOverlay.value.trim();
    if (val) {
      saveHistory();
      const c = canvasRef.current.getContext("2d");
      c.font      = `${fontSize}px ${fontCss}`;
      c.fillStyle = color;
      c.textBaseline = "top";
      c.fillText(val, textOverlay.canvasX, textOverlay.canvasY);
    }
    setTextOverlay(null);
  }, [textOverlay, fontSize, fontCss, color, saveHistory]);

  // ── Calculer position écran de l'input texte ─────
  const computeScreenPos = (canvasX, canvasY) => {
    const canvas = canvasRef.current;
    const area   = areaRef.current;
    if (!canvas || !area) return { screenX: 0, screenY: 0 };
    const cr = canvas.getBoundingClientRect();
    const ar = area.getBoundingClientRect();
    return {
      screenX: (canvasX / canvas.width)  * cr.width  + (cr.left - ar.left),
      screenY: (canvasY / canvas.height) * cr.height + (cr.top  - ar.top),
    };
  };

  // ── Événements pointeur ──────────────────────────
  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    // Valider le texte en cours si on clique ailleurs
    if (textOverlay) { confirmText(); return; }

    const canvas = canvasRef.current;
    const pt = getPos(e, canvas);

    if (tool === "text") {
      const { screenX, screenY } = computeScreenPos(pt.x, pt.y);
      setTextOverlay({ canvasX: pt.x, canvasY: pt.y, screenX, screenY, value: "" });
      return;
    }

    drawing.current = true;
    startPt.current = pt;
    ptsRef.current  = [pt];

    if (tool === "pencil" || tool === "eraser") {
      saveHistory();
      const c = getCtx(tool === "eraser");
      c.beginPath();
      c.moveTo(pt.x, pt.y);
    }
  }, [tool, textOverlay, confirmText, getCtx, saveHistory]);

  const onPointerMove = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const pt = getPos(e, canvasRef.current);
    ptsRef.current.push(pt);
    const { x: sx, y: sy } = startPt.current;

    if (tool === "pencil" || tool === "eraser") {
      const c = getCtx(tool === "eraser");
      c.lineTo(pt.x, pt.y);
      c.stroke();
    } else {
      clearOverlay();
      drawShape(getOvCtx(), tool, sx, sy, pt.x, pt.y);
    }
  }, [tool, getCtx, getOvCtx, drawShape]);

  const onPointerUp = useCallback((e) => {
    if (!drawing.current) return;
    drawing.current = false;
    clearOverlay();
    const canvas = canvasRef.current;
    const pt = e.changedTouches
      ? getPos({ touches: e.changedTouches }, canvas)
      : getPos(e, canvas);
    const { x: sx, y: sy } = startPt.current;

    if (tool === "pencil") {
      if (autoFix && isAlmostStraight(ptsRef.current)) {
        const snap = historyRef.current[historyRef.current.length - 1];
        if (snap) canvas.getContext("2d").putImageData(snap, 0, 0);
        const c = getCtx();
        const { x: ex, y: ey } = snapEnd(sx, sy, pt.x, pt.y);
        c.beginPath(); c.moveTo(sx, sy); c.lineTo(ex, ey); c.stroke();
      }
    } else if (tool !== "eraser") {
      saveHistory();
      drawShape(getCtx(), tool, sx, sy, pt.x, pt.y);
    }
  }, [tool, autoFix, getCtx, drawShape, saveHistory]);

  // ── Envoi ────────────────────────────────────────
  const handleSend = () => {
    if (textOverlay?.value?.trim()) confirmText();
    setTimeout(() => {
      canvasRef.current.toBlob(blob => {
        if (!blob) return;
        onSend(new File([blob], `dessin_${Date.now()}.png`, { type: "image/png" }));
      }, "image/png");
    }, 80);
  };

  const isTextTool = tool === "text";

  return (
    <div className="ql-draw-wrap">
      {/* ── Toolbar ─────────────────────────────── */}
      <div className="ql-draw-toolbar">
        {/* Outils */}
        <div className="ql-draw-tool-group">
          {TOOLS.map(t => (
            <button
              key={t.id}
              className={`ql-draw-tool-btn${tool === t.id ? " active" : ""}`}
              title={t.label}
              onMouseDown={e => { e.preventDefault(); setTool(t.id); if (textOverlay) confirmText(); }}
            >
              <i className={`bi ${t.icon}`}></i>
            </button>
          ))}
        </div>

        <div className="ql-draw-sep"></div>

        {/* Couleurs */}
        <div className="ql-draw-colors">
          {COLORS.map(c => (
            <button
              key={c}
              className={`ql-draw-color${color === c ? " active" : ""}`}
              style={{ background: c }}
              title={c}
              onMouseDown={e => { e.preventDefault(); setColor(c); if (tool === "eraser") setTool("pencil"); }}
            />
          ))}
        </div>

        <div className="ql-draw-sep"></div>

        {/* Taille */}
        <div className="ql-draw-sizes">
          {SIZES.map(s => (
            <button
              key={s}
              className={`ql-draw-size-btn${size === s ? " active" : ""}`}
              title={isTextTool ? `${FONT_SIZES[s]}px` : `${s}px`}
              onMouseDown={e => { e.preventDefault(); setSize(s); }}
            >
              {isTextTool
                ? <span style={{ fontSize: "0.65rem", fontWeight: 700, fontFamily: fontCss, lineHeight: 1 }}>A</span>
                : <span style={{ width: Math.max(s * 1.5, 2), height: Math.max(s * 1.5, 2), borderRadius: "50%", background: "currentColor", display: "block" }}></span>
              }
            </button>
          ))}
        </div>

        {/* Police (visible uniquement pour l'outil Texte) */}
        {isTextTool && (
          <>
            <div className="ql-draw-sep"></div>
            <div className="ql-draw-fonts">
              {FONTS.map(f => (
                <button
                  key={f.id}
                  className={`ql-draw-font-btn${font === f.id ? " active" : ""}`}
                  title={f.label}
                  style={{ fontFamily: f.css }}
                  onMouseDown={e => { e.preventDefault(); setFont(f.id); }}
                >
                  Aa
                </button>
              ))}
            </div>
          </>
        )}

        <div className="ql-draw-sep"></div>

        {/* Auto-correct */}
        <button
          className={`ql-draw-fix-btn${autoFix ? " active" : ""}`}
          title="Correction automatique des lignes"
          onMouseDown={e => { e.preventDefault(); setAutoFix(v => !v); }}
        >
          <i className="bi bi-magic"></i>
          <span>Auto-correct</span>
        </button>

        <div style={{ flex: 1 }}></div>

        <button className="ql-draw-action-btn" disabled={!canUndo} title="Annuler" onMouseDown={e => { e.preventDefault(); undo(); }}>
          <i className="bi bi-arrow-counterclockwise"></i>
        </button>
        <button className="ql-draw-action-btn" title="Effacer tout" onMouseDown={e => { e.preventDefault(); clear(); }}>
          <i className="bi bi-trash"></i>
        </button>
        <button className="btn btn-primary btn-sm ql-draw-send-btn" onMouseDown={e => { e.preventDefault(); handleSend(); }}>
          <i className="bi bi-send-fill me-1"></i>Envoyer
        </button>
        <button className="ql-draw-close-btn" title="Fermer" onMouseDown={e => { e.preventDefault(); onClose(); }}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* ── Canvas area ─────────────────────────── */}
      <div className="ql-draw-canvas-area" ref={areaRef}>
        <canvas
          ref={canvasRef}
          className="ql-draw-canvas"
          width={900} height={500}
          style={{ cursor: isTextTool ? "text" : "crosshair" }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        />
        <canvas
          ref={overlayRef}
          className="ql-draw-canvas ql-draw-overlay"
          width={900} height={500}
          style={{ pointerEvents: "none" }}
        />

        {/* ── Input texte flottant ── */}
        {textOverlay && (
          <div
            className="ql-draw-text-input-wrap"
            style={{ left: textOverlay.screenX, top: textOverlay.screenY }}
          >
            <input
              ref={textInputRef}
              className="ql-draw-text-input"
              value={textOverlay.value}
              placeholder="Tapez puis Entrée…"
              style={{
                fontSize: `${fontSize}px`,
                color: color,
                fontFamily: fontCss,
                minWidth: Math.max(fontSize * 3, 80),
                borderColor: color,
              }}
              onChange={e => setTextOverlay(prev => ({ ...prev, value: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") { e.preventDefault(); confirmText(); }
                if (e.key === "Escape") setTextOverlay(null);
              }}
              onBlur={confirmText}
            />
            <div className="ql-draw-text-hint">↵ Entrée pour valider  ·  Esc pour annuler</div>
          </div>
        )}
      </div>

      {/* ── Aide contextuelle ─────────────────── */}
      <div className="ql-draw-hint">
        {tool === "pencil" && autoFix  && "✨ Tracé libre — lignes presque droites corrigées automatiquement"}
        {tool === "pencil" && !autoFix && "✏️ Tracé libre"}
        {tool === "line"     && "╱ Glissez — ligne snappée sur 0°, 30°, 45°, 60°, 90°…"}
        {tool === "rect"     && "□ Glissez pour tracer un rectangle"}
        {tool === "circle"   && "○ Glissez pour tracer une ellipse"}
        {tool === "triangle" && "△ Glissez pour tracer un triangle"}
        {tool === "text"     && `T Cliquez sur le canvas pour placer du texte (${fontSize}px · ${FONTS.find(f=>f.id===font)?.label})`}
        {tool === "eraser"   && "⌫ Gomme — glissez pour effacer"}
      </div>
    </div>
  );
}
